
/**
 * Черновики текста в чатах: начал писать, ушёл в другой чат или закрыл
 * вкладку — вернулся, а текст на месте (как в Telegram).
 *
 * Хранятся в браузере (localStorage) отдельно для каждого пользователя и
 * чата. Если браузер не даёт хранить (приватный режим) — хотя бы в памяти,
 * пока открыта вкладка.
 */
const PREFIX = 'chatDraft:v1:'
const MAX_LENGTH = 20000
// Черновики старше месяца не храним.
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000

const memory = new Map()
const listeners = new Set()
const notifyTimers = new Map()

const keyOf = (userId, chatId) => `${PREFIX}${String(userId || 'guest')}:${String(chatId || '')}`

const readStorage = (key) => {
    try {
        const raw = window.localStorage.getItem(key)
        if (!raw) return null
        const data = JSON.parse(raw)
        if (!data || typeof data.text !== 'string') return null
        if (Date.now() - Number(data.at || 0) > MAX_AGE_MS) {
            window.localStorage.removeItem(key)
            return null
        }
        return data.text
    } catch (_error) {
        return null
    }
}

const writeStorage = (key, text) => {
    try {
        if (text) window.localStorage.setItem(key, JSON.stringify({ text, at: Date.now() }))
        else window.localStorage.removeItem(key)
    } catch (_error) {
        // Нет места или запрещено — остаётся копия в памяти.
    }
}

const notify = (chatId) => {
    listeners.forEach((listener) => {
        try {
            listener(chatId)
        } catch (_error) {
            // Ошибка одного подписчика не мешает остальным.
        }
    })
}

/** Текст черновика (пусто — черновика нет). */
export const getChatDraft = (userId, chatId) => {
    if (!chatId) return ''
    const key = keyOf(userId, chatId)
    if (memory.has(key)) return memory.get(key)
    const stored = typeof window !== 'undefined' ? readStorage(key) : null
    if (stored) memory.set(key, stored)
    return stored || ''
}

/** Сохранить черновик. Пустой текст удаляет его. */
export const setChatDraft = (userId, chatId, text) => {
    if (!chatId) return
    const key = keyOf(userId, chatId)
    const value = String(text || '').slice(0, MAX_LENGTH)
    const clean = value.trim() ? value : ''
    const previous = memory.has(key) ? memory.get(key) : null
    if (previous === clean || (previous === null && !clean && !readStorage(key))) return

    if (clean) memory.set(key, clean)
    else memory.set(key, '')
    writeStorage(key, clean)

    // Черновик появился или пропал — список чатов обновляем сразу, а правки
    // текста — когда человек остановился, не на каждую букву.
    if (!previous || !clean) {
        clearTimeout(notifyTimers.get(key))
        notifyTimers.delete(key)
        notify(chatId)
        return
    }
    clearTimeout(notifyTimers.get(key))
    notifyTimers.set(key, setTimeout(() => {
        notifyTimers.delete(key)
        notify(chatId)
    }, 500))
}

export const clearChatDraft = (userId, chatId) => setChatDraft(userId, chatId, '')

/** Подписка на изменения (для списка чатов). Возвращает отписку. */
export const subscribeChatDrafts = (listener) => {
    listeners.add(listener)
    return () => listeners.delete(listener)
}

// Черновик поменяли в другой вкладке — обновляем у себя.
if (typeof window !== 'undefined') {
    window.addEventListener('storage', (event) => {
        if (!event.key || !event.key.startsWith(PREFIX)) return
        memory.delete(event.key)
        notify(event.key.slice(event.key.lastIndexOf(':') + 1))
    })
}
